/** @jsxImportSource @emotion/react */
import { useLazyQuery, useMutation } from '@apollo/client'
import Typography from '@mui/material/Typography'
import Modal from '@mui/material/Modal'
import { FC, useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import InsertLinkIcon from '@mui/icons-material/InsertLink'
import IconButton from '@mui/material/IconButton'
import { last } from 'lodash'
import { TAKE_ALL_ITEMS_COUNT } from 'constants/consts'
import { findAllRecommendedArticleGroup_findAllRecommendedArticleGroup_items as Group } from '__generatedTypes__/findAllRecommendedArticleGroup'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import Badge from 'components/Badge'
import DeleteModal from 'components/Modals/DeleteModal'
import Table, { TableProps } from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import Loader from 'components/Loader'
import TabsNavigation from 'components/TabsNavigation'
import { TabsItem } from 'types/tabsItem'
import * as commonClasses from 'components/shared/GroupCards/commonStyles'
import * as classes from 'pagesNew/FlashCards/FlashCardsTabs/styles'
import {
  BULK_REMOVE_RECOMMENDED_ARTICLES,
  BULK_UPDATE_RECOMMENDED_ARTICLE,
  FIND_ALL_RECOMMENDED_ARTICLES,
} from 'queries/recommendedArticle'
import {
  findAllRecommendedArticle,
  findAllRecommendedArticleVariables,
  findAllRecommendedArticle_findAllRecommendedArticle_items as RecommendedArticle,
} from '__generatedTypes__/findAllRecommendedArticle'
import {
  bulkUpdateRecommendedArticle,
  bulkUpdateRecommendedArticleVariables,
} from '__generatedTypes__/bulkUpdateRecommendedArticle'
import {
  bulkRemoveRecommendedArticle,
  bulkRemoveRecommendedArticleVariables,
} from '__generatedTypes__/bulkRemoveRecommendedArticle'
import { toast } from 'utils/toast'
import * as articlesClasses from '../RecommendedArticlesGroups/styles'
import { ManageRecommendedArticleModal } from './ManageRecommendedArticleModal'

interface Props {
  groups: Group[]
  initialSelected: Group
  selectedSubjectId: string
}

export const CARD_GROUP_QUERY_PARAM = 'card-group'

const RecommendedArticlesTabs: FC<Props> = ({ groups, selectedSubjectId, initialSelected }) => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [getRecommendedArticles, { data: recommendedArticlesData, loading }] = useLazyQuery<
    findAllRecommendedArticle,
    findAllRecommendedArticleVariables
  >(FIND_ALL_RECOMMENDED_ARTICLES)
  const [articlesCards, setArticlesCards] = useState<RecommendedArticle[]>([])
  const [selectedGroup, setSelectedGroup] = useState<Group>(initialSelected)
  const [isCardModalOpen, setCardModalOpen] = useState<boolean>(false)
  const [selectedArticle, setSelectedArticle] = useState<RecommendedArticle | null>(null)
  const [isBulkDeleteModalOpen, setBulkDeleteModalOpen] = useState<boolean>(false)
  const [articleCardsIdsToDelete, setArticleCardsIdsToDelete] = useState<RecommendedArticle[]>([])
  const [bulkUpdateArticles] = useMutation<bulkUpdateRecommendedArticle, bulkUpdateRecommendedArticleVariables>(
    BULK_UPDATE_RECOMMENDED_ARTICLE
  )
  const [bulkRemoveArticles] = useMutation<bulkRemoveRecommendedArticle, bulkRemoveRecommendedArticleVariables>(
    BULK_REMOVE_RECOMMENDED_ARTICLES
  )

  const initializeRecommendedArticles = async () => {
    if (selectedGroup) {
      const { data: articleCardsResponse } = await getRecommendedArticles({
        variables: {
          query: {
            take: TAKE_ALL_ITEMS_COUNT,
            where: {
              recommendedArticleGroup: {
                id: selectedGroup.id,
              },
            },
          },
        },
        fetchPolicy: 'cache-and-network',
      })

      setArticlesCards(articleCardsResponse?.findAllRecommendedArticle.items || [])
    }
  }

  useEffect(() => {
    setArticleCardsIdsToDelete([])
    initializeRecommendedArticles()
  }, [selectedGroup])

  useEffect(() => {
    if (selectedGroup && !groups.find((group) => group.id === selectedGroup.id)) {
      setSelectedGroup(groups[0])
    }
  }, [groups])

  useEffect(() => {
    setArticlesCards([])
  }, [selectedSubjectId])

  useEffect(() => {
    recommendedArticlesData && setArticlesCards(recommendedArticlesData.findAllRecommendedArticle.items || [])
  }, [recommendedArticlesData])

  const handleModalClose = () => {
    setSelectedArticle(null)
    setCardModalOpen(false)
  }

  const handleEditClick = (recommendedArticle: RecommendedArticle) => {
    setSelectedArticle(recommendedArticle)
    setCardModalOpen(true)
    setArticleCardsIdsToDelete([])
  }

  const handleBulkDelete = useCallback(async () => {
    const ids = articleCardsIdsToDelete.map((group) => ({ id: group.id }))

    try {
      await bulkRemoveArticles({ variables: { ids } })
      setArticleCardsIdsToDelete([])
      setBulkDeleteModalOpen(false)

      initializeRecommendedArticles()

      toast.success('Cards deleted successfully')
    } catch (error) {
      toast.error('Failed to delete cards')
    }
  }, [articleCardsIdsToDelete])

  const handleChangeGroup = ({ id }: TabsItem) => {
    const selectedGroup = groups.find(({ id: groupId }) => groupId === id)

    if (selectedGroup) {
      setSelectedGroup(selectedGroup)

      searchParams.set(CARD_GROUP_QUERY_PARAM, selectedGroup.id)
      setSearchParams(searchParams, { replace: true })
    }
  }

  const handleSort = useCallback(async (items: RecommendedArticle[]) => {
    try {
      if (items.length > 0) {
        setArticlesCards(items)

        await bulkUpdateArticles({
          variables: {
            bulkUpdateRecommendedArticleInput: items.map(({ id }, order) => ({ id, order })),
          },
        })

        toast.success('Cards order updated successfully')
      }
    } catch (error) {
      toast.error('Failed to update cards order')
    }
  }, [])

  const RecommendedArticlesCardsColumn: TableProps<RecommendedArticle>['columns'] = [
    {
      title: 'Title',
      renderCell: (article) => (
        <Typography css={commonClasses.longText} variant="body2" data-testid="card-title">
          {article.name}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Description',
      renderCell: (article) => (
        <Typography css={commonClasses.longText} variant="body2">
          {article.description}
        </Typography>
      ),
      flex: '0 1 300px',
    },
    {
      title: 'Group',
      renderCell: (article) => (
        <Badge
          text={article.recommendedArticleGroup.title}
          color={article.recommendedArticleGroup.color}
          css={classes.badge}
        />
      ),
      flex: '0 1 250px',
    },
    {
      title: 'URL',
      renderCell: (article) => (
        <IconButton size="small" href={article.link} target="_blank" css={classes.link}>
          <InsertLinkIcon />
        </IconButton>
      ),
      flex: '0 1 100px',
    },
    {
      renderCell: (article) => (
        <EditIcon css={commonClasses.editIcon} onClick={() => handleEditClick(article)} data-testid="edit-card-btn" />
      ),
      flex: '0 1 29px',
    },
  ]

  const renderContent = () => {
    if (!groups?.length) {
      return null
    }

    return (
      <>
        <div css={classes.tabsBlock}>
          <TabsNavigation tabs={groups} value={selectedGroup} onChange={handleChangeGroup} css={classes.tabsBlock} />
        </div>
        {loading ? (
          <Loader />
        ) : (
          <Table
            css={articlesClasses.table}
            columns={RecommendedArticlesCardsColumn}
            items={articlesCards}
            isSelectable
            selectedItems={articleCardsIdsToDelete}
            onSelect={setArticleCardsIdsToDelete}
            onSort={handleSort}
            isSortable
          />
        )}
      </>
    )
  }

  const lastGroupOrder = last(articlesCards)?.order

  return (
    <div>
      <Typography css={classes.title} variant="title3" component="p">
        Cards
      </Typography>
      {renderContent()}
      {selectedGroup && (
        <TableButtons
          itemName="Cards"
          areItemsToDelete={articleCardsIdsToDelete.length !== 0}
          onAddItem={() => setCardModalOpen(true)}
          onCancel={() => setArticleCardsIdsToDelete([])}
          onBatchDelete={() => setBulkDeleteModalOpen(true)}
        />
      )}
      <Modal open={!!isCardModalOpen} onClose={handleModalClose}>
        <ManageRecommendedArticleModal
          onClose={handleModalClose}
          recommendedArticleGroups={groups}
          recommendedArticle={selectedArticle ?? { id: 'id', recommendedArticleGroup: { id: selectedGroup.id } }}
          newGroupOrder={lastGroupOrder !== undefined ? lastGroupOrder + 1 : 0}
          isEditMode={!!selectedArticle}
        />
      </Modal>
      <DeleteModal
        open={isBulkDeleteModalOpen}
        count={articleCardsIdsToDelete.length}
        onClose={() => setBulkDeleteModalOpen(false)}
        onConfirm={handleBulkDelete}
      />
    </div>
  )
}

export default RecommendedArticlesTabs
